import { Button, DatePicker, Form, Input, Radio, Select, Space } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BDFlag from "../../../assets/icons/BDFlag";
import DateIcon from "../../../assets/icons/DateIcon";
import FileIcon from "../../../assets/icons/FileIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import {
  bloodGroups,
  dateFormat,
  dbDateFormat,
  genderList,
} from "../../../constants/constants";
import {
  useGetEmployeeDetailsQuery,
  useUpdateEmployeePersonalDataMutation,
} from "../../../features/employee/employeeApi";
import useError from "../../../hooks/useError";
import { disableFutureDates } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { formatUrl } from "../../../utils/string";
import {
  formatPhoneNumber,
  removeHttps,
  selectSearchOption,
  selectStringOption,
  urlValidation,
} from "../../../utils/utils";
import InfoTitle from "../../common/InfoTitle";
import EmployeeFileUpload from "../EmployeeFileUpload";
import { replaceEditWithView } from "./utils";

export default function PersonalInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { editId } = useParams();
  const {
    isError: isGetEmployeeDetailsError,
    error: getEmployeeDetailsError,
    data: getEmployeeDetails,
  } = useGetEmployeeDetailsQuery(editId || "", {
    skip: !editId,
  });
  const [updateEmployeePersonalData, { isSuccess, isLoading, isError, error }] =
    useUpdateEmployeePersonalDataMutation();
  useError(
    isGetEmployeeDetailsError,
    getEmployeeDetailsError,
    "Get employee data fail"
  );
  useError(isError, error, "Update personal information fail");

  const employeeDetails = getEmployeeDetails?.data?.employee;

  const onFinish = (values: any) => {
    const { dob, social_links, nid_no, mobile, ...rest } = values;

    const newData = {
      ...rest,
      nid_no: nid_no?.toString(),
      dob: dob ? dob?.format(dbDateFormat) : null,
      mobile: formatPhoneNumber(mobile),
      social_links: {
        facebook: formatUrl(social_links?.facebook),
        instagram: formatUrl(social_links?.instagram),
        linkedIn: formatUrl(social_links?.linkedIn),
      },
      // fb_id: ,
      member_id: employeeDetails?.member_id,
      profile_id: employeeDetails?.member?.profile?.id,
    };

    if (editId)
      updateEmployeePersonalData({
        body: newData,
        id: editId,
      });
  };

  useEffect(() => {
    if (Object.keys(employeeDetails || {})) {
      form.setFieldsValue({
        gender: employeeDetails?.member?.profile?.gender,
        mobile: employeeDetails?.mobile,
        dob: employeeDetails?.member?.profile?.dob
          ? dayjs(employeeDetails?.member?.profile?.dob)
          : undefined,
        address: employeeDetails?.member?.profile?.address,
        nationality: employeeDetails?.member?.profile?.nationality,
        nid_no: employeeDetails?.member?.profile?.nid_no
          ? Number(employeeDetails?.member?.profile?.nid_no)
          : undefined,
        nid_image_back: employeeDetails?.member?.profile?.nid_image_back,
        nid_image_front: employeeDetails?.member?.profile?.nid_image_front,
        e_signature: employeeDetails?.member?.profile?.e_signature,
        passport_no: employeeDetails?.member?.profile?.passport_no,
        passport_image: employeeDetails?.member?.profile?.passport_image,
        blood_group: employeeDetails?.member?.profile?.blood_group,
        social_links: {
          facebook: removeHttps(
            employeeDetails?.member?.social_links?.facebook
          ),
          instagram: removeHttps(
            employeeDetails?.member?.social_links?.instagram
          ),
          linkedIn: removeHttps(
            employeeDetails?.member?.social_links?.linkedIn
          ),
        },
      });
    }
  }, [employeeDetails, form]);

  const backLink = replaceEditWithView(location.pathname + location.search);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Personal Information updated successfully!");
      navigate(backLink);
    }
  }, [backLink, isSuccess, navigate]);

  return (
    <div>
      <InfoTitle className="mb-4" icon={<FileIcon />}>
        Personal Information
      </InfoTitle>
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <div className="flex items-center gap-6 mb-2">
          <h6 className="text-header text-sm font-medium">Gender:</h6>
          <Form.Item
            name="gender"
            rules={[
              {
                required: true,
                message: "Please select gender",
              },
            ]}
            className="mb-0"
          >
            <Radio.Group className="custom-radio !h-auto" size="large" disabled>
              {genderList?.map((el, idx) => (
                <Radio
                  className="text-header text-sm font-medium mr-3.5"
                  value={el}
                  key={idx}
                >
                  {el}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </div>
        <div className="mb-3">
          <Form.Item
            name="mobile"
            label="Contact Number"
            className="mb-1"
            initialValue="+880"
            rules={[
              {
                validator: async (_: any, value: string) => {
                  if (!value) {
                    return Promise.reject(
                      new Error("Please enter phone number")
                    );
                  }
                  // if (!value) {
                  //   return Promise.resolve();
                  // }

                  const phoneRegex = /^(\+88)?(01[3-9])[0-9]{8}$/;
                  if (!phoneRegex.test(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid Bangladeshi phone number")
                    );
                  }
                },
              },
            ]}
          >
            <Input
              prefix={<BDFlag />}
              size="large"
              // placeholder="+8801"
              disabled
            />
          </Form.Item>
          <small className="block text-body-text text-sm">
            Please contact with the co-worker if the number needs to be changed
          </small>
        </div>
        <Form.Item
          name="dob"
          label="Date of Birth"
          className="mb-4"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please select date of birth",
          //   },
          // ]}
        >
          <DatePicker
            // defaultValue={dayjs(formatDate(), dateFormat)}
            format={dateFormat}
            suffixIcon={<DateIcon width={18} height={20} />}
            style={{ width: "100%" }}
            size="large"
            disabledDate={disableFutureDates}
            disabled
          />
        </Form.Item>
        <Form.Item
          name="address"
          label="Address"
          className="mb-4"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please enter address",
          //   },
          // ]}
        >
          <Input
            size="large"
            // placeholder="Enter your address"
            disabled
          />
        </Form.Item>
        <Form.Item
          name="nationality"
          label="Nationality"
          className="mb-4"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please enter nationality",
          //   },
          // ]}
        >
          <Input
            size="large"
            // placeholder="Enter your nationality"
            disabled
          />
        </Form.Item>
        <Form.Item
          name="nid_no"
          label="NID Number"
          className="mb-4"
          rules={[
            {
              validator: async (_: any, value: string) => {
                // if (!value) {
                //   return Promise.reject(new Error("Please enter NID"));
                // }

                if (!value) {
                  return Promise.resolve();
                }

                if (Number(value) < 0) {
                  return Promise.reject(new Error("NID can't be negative"));
                }

                if (!Number.isInteger(Number(value))) {
                  return Promise.reject(new Error("NID must be an integer"));
                }

                if (value.length < 10) {
                  return Promise.reject(new Error("NID should be minimum 10"));
                }
                if (value.length > 19) {
                  return Promise.reject(new Error("NID should be maximum 19"));
                }
              },
            },
          ]}
        >
          <Input
            className="w-full"
            size="large"
            // placeholder="Ex: 12345"
            disabled
          />
        </Form.Item>

        <Form.Item
          name="nid_image_front"
          label="NID Front Image"
          className="mb-4"
          valuePropName="fileUrl"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please upload NID photo",
          //   },
          // ]}
        >
          <EmployeeFileUpload
            form={form}
            accept=".png, .jpg, .jpeg, .gif"
            name="nid_image_front"
            onChange={(url) =>
              form.setFieldsValue({
                nid_image_front: url,
              })
            }
            disabled
          />
        </Form.Item>
        <Form.Item
          name="nid_image_back"
          label="NID Back Image"
          className="mb-4"
          valuePropName="fileUrl"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please upload NID photo",
          //   },
          // ]}
        >
          <EmployeeFileUpload
            form={form}
            accept=".png, .jpg, .jpeg"
            name="nid_image_back"
            onChange={(url) =>
              form.setFieldsValue({
                nid_image_back: url,
              })
            }
            disabled
          />
        </Form.Item>

        <Form.Item
          name="passport_no"
          label="Passport Number"
          className="mb-4"
          rules={[
            {
              validator: async (_: any, value: string) => {
                // if (!value) {
                //   return Promise.reject(
                //     new Error("Please enter passport number")
                //   );
                // }

                if (!value) {
                  return Promise.resolve();
                }

                const passportRegex = /^[A-za-z0-9]{5,15}$/;
                if (!passportRegex.test(value)) {
                  return Promise.reject(
                    new Error("Please enter a valid passport number")
                  );
                }
              },
            },
          ]}
        >
          <Input
            size="large"
            // placeholder="Ex: 12345"
            disabled
          />
        </Form.Item>

        <Form.Item
          name="passport_image"
          label="Passport Copy"
          className="mb-4"
          valuePropName="fileUrl"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please upload passport photo",
          //   },
          // ]}
        >
          <EmployeeFileUpload
            form={form}
            accept=".png, .jpg, .jpeg"
            name="passport_image"
            onChange={(url) =>
              form.setFieldsValue({
                passport_image: url,
              })
            }
            disabled
          />
        </Form.Item>

        <Form.Item
          name="e_signature"
          label="e-Signature"
          className="mb-4"
          valuePropName="fileUrl"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please upload e-signature photo",
          //   },
          // ]}
        >
          <EmployeeFileUpload
            form={form}
            accept=".png, .jpg, .jpeg"
            name="e_signature"
            onChange={(url) =>
              form.setFieldsValue({
                e_signature: url,
              })
            }
            disabled
          />
        </Form.Item>

        <Form.Item
          name="blood_group"
          label="Blood Group"
          className="mb-4"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please select blood group",
          //   },
          // ]}
        >
          <Select
            // placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={selectStringOption(bloodGroups)}
            suffixIcon={<SelectDropdownLgIcon />}
            disabled
          />
        </Form.Item>

        <Form.Item
          name={["social_links", "facebook"]}
          label="Facebook"
          className="mb-4"
          rules={[
            // {
            //   required: true,
            //   message: "Please enter facebook URL",
            // },
            {
              validator: urlValidation,
            },
          ]}
        >
          <Input
            size="large"
            prefix="https://"
            // placeholder="link.com/"
            disabled
          />
        </Form.Item>
        <Form.Item
          name={["social_links", "instagram"]}
          label="Instagram"
          className="mb-4"
          rules={[
            // {
            //   required: true,
            //   message: "Please enter instagram URL",
            // },
            {
              validator: urlValidation,
            },
          ]}
        >
          <Input
            size="large"
            prefix="https://"
            // placeholder="link.com/"
            disabled
          />
        </Form.Item>
        <Form.Item
          name={["social_links", "linkedIn"]}
          label="Linkedin"
          className="mb-4"
          rules={[
            // {
            //   required: true,
            //   message: "Please enter linkedin URL",
            // },
            {
              validator: urlValidation,
            },
          ]}
        >
          <Input
            size="large"
            prefix="https://"
            // placeholder="link.com/"
            disabled
          />
        </Form.Item>

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={() => navigate(backLink)}
            size="middle"
            disabled={isLoading}
          >
            Cancel
          </Button>
          {/* <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
          >
            Update
          </Button> */}
        </Space>
      </Form>
    </div>
  );
}
